/**
 * User Notices API Endpoints
 * Integrates with /api/user-notices backend endpoints
 */

import apiClient from '../client';
import type {
  UserNotice,
  CreateUserNoticeRequest,
} from '../types/notices';
import { NoticeStatus, NoticeTypeIds } from '../types/notices';

/**
 * Transform backend notice to UI-compatible format
 */
function transformNotice(notice: any): UserNotice {
  // Parse data JSON if present
  let parsedData = undefined;
  if (notice.data) {
    try {
      parsedData = JSON.parse(notice.data);
    } catch (e) {
      console.warn('[transformNotice] Failed to parse data:', e);
    }
  }

  // Parse actionsJson from backend if present
  let backendActions: any[] | undefined;
  if (notice.actionsJson) {
    try {
      const parsed = JSON.parse(notice.actionsJson);
      if (Array.isArray(parsed)) {
        backendActions = parsed.map((a: any) => ({
          label: a.Label || a.label,
          actionType: a.ActionType || a.actionType,
          actionData: a.ActionData || a.actionData,
        }));
      }
    } catch (e) {
      console.warn('[transformNotice] Failed to parse actionsJson:', e);
    }
  }

  // Generate actions based on notice type, fall back to backend-provided actions
  const actions = generateActionsForNoticeType(notice.type, parsedData) || backendActions;

  return {
    ...notice,
    timestamp: notice.created,
    message: notice.quickMessage,
    read: notice.status >= NoticeStatus.Read,
    acknowledged: notice.status >= NoticeStatus.Acknowledged,
    actions,
    parsedData,
  };
}

/**
 * Generate action buttons based on notice type
 */
function generateActionsForNoticeType(noticeType: string, parsedData: any): any[] | undefined {
  const typeUpper = noticeType?.toUpperCase();

  switch (typeUpper) {
    case NoticeTypeIds.FRIEND_REQUEST.toUpperCase(): {
      const connectionId = parsedData?.connectionRequestId || parsedData?.ConnectionRequestId;
      const requesterId = parsedData?.requesterId || parsedData?.RequesterId;

      if (connectionId || requesterId) {
        return [
          {
            label: 'Accept',
            actionType: 'accept_friend_request',
            actionData: connectionId || requesterId,
          },
          {
            label: 'Decline',
            actionType: 'decline_friend_request',
            actionData: connectionId || requesterId,
          },
          {
            label: 'Block',
            actionType: 'block_user',
            actionData: requesterId || connectionId,
          },
        ];
      }
      break;
    }

    case NoticeTypeIds.GROUP_INVITATION.toUpperCase(): {
      const groupId = parsedData?.groupId || parsedData?.GroupId;

      if (groupId) {
        return [
          {
            label: 'Join',
            actionType: 'accept_group_invite',
            actionData: groupId,
          },
          {
            label: 'Decline',
            actionType: 'decline_group_invite',
            actionData: groupId,
          },
          {
            label: 'Block',
            actionType: 'block_group',
            actionData: groupId,
          },
        ];
      }
      break;
    }

    case NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase(): {
      const adventureId = parsedData?.adventureId || parsedData?.AdventureId;

      if (adventureId) {
        return [
          {
            label: 'Join',
            actionType: 'accept_adventure_invite',
            actionData: adventureId,
          },
          {
            label: 'Decline',
            actionType: 'decline_adventure_invite',
            actionData: adventureId,
          },
        ];
      }
      break;
    }

    case NoticeTypeIds.SESSION_INVITE.toUpperCase(): {
      const sessionId = parsedData?.SessionId || parsedData?.sessionId;

      if (sessionId) {
        return [
          {
            label: 'Accept',
            actionType: 'accept_session_invite',
            actionData: sessionId,
          },
          {
            label: 'Decline',
            actionType: 'decline_session_invite',
            actionData: sessionId,
          },
        ];
      }
      break;
    }

    case NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase(): {
      const adventureId = parsedData?.adventureId || parsedData?.AdventureId;
      if (adventureId) {
        return [
          {
            label: 'Review',
            actionType: 'view_moderator_requests',
            actionData: adventureId,
          },
        ];
      }
      break;
    }

    // No actions for these types
    case NoticeTypeIds.FRIEND_REQUEST_ACCEPTED.toUpperCase():
    case NoticeTypeIds.GENERAL.toUpperCase():
    case NoticeTypeIds.SYSTEM.toUpperCase():
      return undefined;
  }

  return undefined;
}

/**
 * Get all user notices
 * GET /api/user-notices
 */
export const getUserNotices = async (): Promise<UserNotice[]> => {
  const response = await apiClient.get('/api/user-notices');
  let data = response.data;

  console.log('[getUserNotices] Response type:', typeof data);
  console.log('[getUserNotices] Is array?:', Array.isArray(data));

  // Handle case where Axios didn't parse JSON (backend sent wrong Content-Type)
  if (typeof data === 'string') {
    console.log('[getUserNotices] Data is string, attempting to parse JSON...');
    try {
      // Parse in chunks to handle large payloads with circular refs
      data = JSON.parse(data);
      console.log('[getUserNotices] Successfully parsed JSON string');
    } catch (error) {
      console.error('[getUserNotices] Failed to parse JSON:', error);
      // Try to extract just the main array without circular refs
      try {
        // Find the start and end of the main array
        const arrayMatch = data.match(/^\[.*?\}(?=,\{"id"|])/);
        if (arrayMatch) {
          data = JSON.parse(arrayMatch[0] + ']');
          console.log('[getUserNotices] Recovered partial array');
        }
      } catch (e2) {
        console.error('[getUserNotices] Recovery failed:', e2);
        return [];
      }
    }
  }

  // If response is already an array, return it
  if (Array.isArray(data)) {
    console.log('[getUserNotices] Returning array with length:', data.length);
    return data.map(transformNotice);
  }

  // If response has a notices property, return that
  if (data && Array.isArray(data.notices)) {
    console.log('[getUserNotices] Returning data.notices with length:', data.notices.length);
    return data.notices.map(transformNotice);
  }

  // If response has a data property, return that
  if (data && Array.isArray(data.data)) {
    console.log('[getUserNotices] Returning data.data with length:', data.data.length);
    return data.data.map(transformNotice);
  }

  // Default to empty array
  console.warn('[getUserNotices] Unexpected response format. Type:', typeof data);
  return [];
};

/**
 * Create a new user notice
 * POST /api/user-notices
 */
export const createUserNotice = async (
  request: CreateUserNoticeRequest
): Promise<void> => {
  await apiClient.post('/api/user-notices', request);
};

/**
 * Get unread notices count
 * GET /api/user-notices/unread-count
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get('/api/user-notices/unread-count');
  const data = response.data;

  console.log('[getUnreadCount] Raw response:', data);
  console.log('[getUnreadCount] Response type:', typeof data);

  // Handle different response structures
  if (typeof data === 'number') {
    console.log('[getUnreadCount] Returning plain number:', data);
    return data;
  }

  if (data && typeof data.count === 'number') {
    console.log('[getUnreadCount] Returning data.count:', data.count);
    return data.count;
  }

  if (data && typeof data.unreadCount === 'number') {
    console.log('[getUnreadCount] Returning data.unreadCount:', data.unreadCount);
    return data.unreadCount;
  }

  console.warn('[getUnreadCount] Unexpected response format from /api/user-notices/unread-count:', data);
  return 0;
};

/**
 * Mark a notice as read
 * PUT /api/user-notices/{noticeId}/read
 */
export const markNoticeAsRead = async (noticeId: string): Promise<void> => {
  await apiClient.put(`/api/user-notices/${noticeId}/read`);
};

/**
 * Mark a notice as acknowledged
 * PUT /api/user-notices/{noticeId}/acknowledged
 */
export const markNoticeAsAcknowledged = async (
  noticeId: string
): Promise<void> => {
  await apiClient.put(`/api/user-notices/${noticeId}/acknowledged`);
};

/**
 * Delete a notice
 * DELETE /api/user-notices/{noticeId}
 */
export const deleteNotice = async (noticeId: string): Promise<void> => {
  await apiClient.delete(`/api/user-notices/${noticeId}`);
};
